import {useState, useEffect, useCallback} from 'react';
import {
    ACTIVE_ROLES_CHANGED_EVENT,
    clearActiveRoles,
    getActiveRoles,
    setActiveRoles,
    UserRoleType,
    areRolesEqual,
    getHighestPriorityRole,
} from '@/config/roles';
import {appConfig} from '@/config/appConfig';

interface UserLike {
    allAuthorities?: string[];
    authorities?: string[];
}

/**
 * Mirrors hse-skeleton-app's useUserRoles, adapted to remus-web's Redux user.
 *
 * Modes (driven by appConfig.activeRolesFeature):
 * - Disabled (active: false)        → all JWT roles always active, no UI controls.
 * - Multi-select (active, !exclusive) → toggle multiple, min 1 active, defaults to all.
 * - Exclusive   (active,  exclusive) → exactly one active, defaults to highest priority.
 *
 * Active roles are persisted to localStorage and synchronized across mounted hooks.
 * REMUS also mirrors changes into its init Redux state so legacy consumers of
 * user.authorities update immediately. Backend authorization is unaffected (JWT only).
 */
export const useUserRoles = (user: UserLike | null | undefined) => {
    const sourceRoles: UserRoleType[] = (user?.allAuthorities ?? user?.authorities ?? []) as UserRoleType[];
    const [activeRoles, setActiveRolesState] = useState<UserRoleType[]>([]);

    useEffect(() => {
        if (sourceRoles.length === 0) {
            setActiveRolesState([]);
            return;
        }

        if (!appConfig.activeRolesFeature.active) {
            setActiveRolesState(sourceRoles);
            return;
        }

        const stored = getActiveRoles();

        if (appConfig.activeRolesFeature.exclusiveMode) {
            if (stored.length === 0) {
                const highest = getHighestPriorityRole(sourceRoles);
                const next = highest ? [highest] : sourceRoles;
                setActiveRolesState(next);
                setActiveRoles(next);
            } else {
                const validRole = stored.find(role => sourceRoles.includes(role));
                if (validRole) {
                    setActiveRolesState([validRole]);
                } else {
                    const highest = getHighestPriorityRole(sourceRoles);
                    const next = highest ? [highest] : sourceRoles;
                    setActiveRolesState(next);
                    setActiveRoles(next);
                }
            }
            return;
        }

        if (stored.length === 0) {
            setActiveRolesState(sourceRoles);
            setActiveRoles(sourceRoles);
            return;
        }

        const valid = stored.filter(role => sourceRoles.includes(role));
        if (valid.length === 0) {
            setActiveRolesState(sourceRoles);
            setActiveRoles(sourceRoles);
        } else {
            setActiveRolesState(valid);
            if (valid.length !== stored.length) {
                setActiveRoles(valid);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceRoles.join('|')]);

    useEffect(() => {
        if (!appConfig.activeRolesFeature.active || typeof window === 'undefined') return;

        const normalizeActiveRoles = (roles: UserRoleType[]): UserRoleType[] => {
            const valid = roles.filter(role => sourceRoles.includes(role));
            if (valid.length > 0) {
                return appConfig.activeRolesFeature.exclusiveMode ? [valid[0]] : valid;
            }
            if (appConfig.activeRolesFeature.exclusiveMode) {
                const highest = getHighestPriorityRole(sourceRoles);
                return highest ? [highest] : sourceRoles;
            }
            return sourceRoles;
        };

        const syncActiveRoles = (roles: UserRoleType[]) => {
            const next = normalizeActiveRoles(roles);
            setActiveRolesState(previous =>
                areRolesEqual(previous, next) ? previous : next
            );
        };

        const handleActiveRolesChanged = (event: Event) => {
            const customEvent = event as CustomEvent<UserRoleType[]>;
            syncActiveRoles(customEvent.detail ?? getActiveRoles());
        };
        const handleStorageChanged = () => syncActiveRoles(getActiveRoles());

        window.addEventListener(ACTIVE_ROLES_CHANGED_EVENT, handleActiveRolesChanged as EventListener);
        window.addEventListener('storage', handleStorageChanged);

        return () => {
            window.removeEventListener(
                ACTIVE_ROLES_CHANGED_EVENT,
                handleActiveRolesChanged as EventListener
            );
            window.removeEventListener('storage', handleStorageChanged);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceRoles.join('|')]);

    const hasRole = useCallback(
        (role: string): boolean => sourceRoles.includes(role as UserRoleType),
        [sourceRoles]
    );

    const hasActiveRole = useCallback(
        (role: string): boolean => {
            if (!appConfig.activeRolesFeature.active) {
                return hasRole(role);
            }
            return activeRoles.includes(role as UserRoleType);
        },
        [activeRoles, hasRole]
    );

    const toggleActiveRole = useCallback(
        (role: UserRoleType) => {
            if (!appConfig.activeRolesFeature.active) return;
            if (!sourceRoles.includes(role)) return;

            if (appConfig.activeRolesFeature.exclusiveMode) {
                const next = [role];
                setActiveRoles(next);
                setActiveRolesState(next);
                return;
            }

            const next = activeRoles.includes(role)
                ? activeRoles.filter(r => r !== role)
                : [...activeRoles, role];

            if (next.length === 0) {
                console.warn('Cannot deselect all roles');
                return;
            }

            setActiveRoles(next);
            setActiveRolesState(next);
        },
        [activeRoles, sourceRoles]
    );

    const resetActiveRoles = useCallback(() => {
        if (!appConfig.activeRolesFeature.active) return;
        if (sourceRoles.length === 0) return;

        if (appConfig.activeRolesFeature.exclusiveMode) {
            const highest = getHighestPriorityRole(sourceRoles);
            const next = highest ? [highest] : sourceRoles;
            setActiveRoles(next);
            setActiveRolesState(next);
        } else {
            setActiveRoles(sourceRoles);
            setActiveRolesState(sourceRoles);
        }
    }, [sourceRoles]);

    const isAllRolesActive = useCallback(
        () => sourceRoles.length > 0 && areRolesEqual(activeRoles, sourceRoles),
        [activeRoles, sourceRoles]
    );

    return {
        allRoles: sourceRoles,
        activeRoles,
        hasRole,
        hasActiveRole,
        toggleActiveRole,
        resetActiveRoles,
        clearActiveRoles,
        isAllRolesActive,
    };
};
